// utils/jobDataSelector.js
export const getEssentialJobData = (jobData) => {
  if (!jobData) return { job: null };

  // Return already optimized data as-is
  if (jobData.__optimized) return jobData;

  const rawJobData = jobData.job || jobData;

  const safe = (value, fallback = null) => (value !== undefined && value !== null ? value : fallback);

  const removeKey = (obj, key) => {
    if (!obj) return null;
    const cloned = { ...obj }; // clone to avoid mutating original
    delete cloned[key];        // remove the key safely
    return cloned;
  };

  const essentialData = {
    job: {
      id: safe(rawJobData.id),
      startDate: safe(rawJobData.startDate),
      endDate: safe(rawJobData.endDate),
      isBlockShift: safe(rawJobData.isBlockShift, false),
      maximumApplicationLimit: safe(rawJobData.maximumApplicationLimit, 0),
      recommendedRate: safe(rawJobData.recommendedRate),
      recommendedWeekendRate: safe(rawJobData.recommendedWeekendRate),
      maxTravelCompensation: safe(rawJobData.maxTravelCompensation, 0),
      enableTravelAllowanceForFirstAndLastDay: safe(rawJobData.enableTravelAllowanceForFirstAndLastDay, false),
      blockedByLocum: safe(rawJobData.blockedByLocum, false),
      openMarketplaceShiftForInternalLocum: safe(rawJobData.openMarketplaceShiftForInternalLocum, false),
      isInternalStaffOfThisPharmacy: safe(rawJobData.isInternalStaffOfThisPharmacy, false),
      isVisibileAsInternalLocum: safe(rawJobData.isVisibileAsInternalLocum, false),
      recommendedRatesString: safe(rawJobData.recommendedRatesString, ""),
      minRate: safe(rawJobData.minRate),
      maxRate: safe(rawJobData.maxRate),
      weekendMinRate: safe(rawJobData.weekendMinRate),
      weekendMaxRate: safe(rawJobData.weekendMaxRate),
      saturdayMinRate: safe(rawJobData.saturdayMinRate),
      saturdayMaxRate: safe(rawJobData.saturdayMaxRate),
      sundayMinRate: safe(rawJobData.sundayMinRate),
      sundayMaxRate: safe(rawJobData.sundayMaxRate),
      distance: safe(rawJobData.distance),
      covidCertRequired: safe(rawJobData.covidCertRequired, false),
      platformRoleId: safe(rawJobData.platformRoleId),
      platformRole: safe(rawJobData.platformRole, null),
      pharmacyId: safe(rawJobData.pharmacyId),
      applyJob: safe(rawJobData.applyJob, false),

      // Profile booking gate (pharmacy-scoped; must not be stripped from store)
      profileRequirements: safe(rawJobData.profileRequirements, null),
      isContractorForPharmacy: safe(rawJobData.isContractorForPharmacy, false),
      contractorInvoicingEnabled: safe(rawJobData.contractorInvoicingEnabled, false),

      // Pharmacy
      pharmacy: safe(rawJobData.pharmacy, null),

      // Settings
      settings: rawJobData.settings
        ? {
            payingForTravel: safe(rawJobData.settings.payingForTravel, false),
            languages: safe(rawJobData.settings.languages, []),
            languageNames: safe(rawJobData.settings.languageNames, []),
            solo: safe(rawJobData.settings.solo, false),
            numOfTech: safe(rawJobData.settings.numOfTech, 0),
            payForFlights: safe(rawJobData.settings.payForFlights, false),
            payForAccommodation: safe(rawJobData.settings.payForAccommodation, false),
            adminAids: safe(rawJobData.settings.adminAids, []),
            openUp: safe(rawJobData.settings.openUp, null),
            close: safe(rawJobData.settings.close, null),
            notes: safe(rawJobData.settings.notes, ""),
            parking: safe(rawJobData.settings.parking, false)
          }
        : null,

      abilities: safe(rawJobData.abilities, []),
      config: safe(rawJobData.config, {}),

      // Agency
      agency: safe(rawJobData.agency, null),

      // Shifts
      shifts: rawJobData.shifts
        ? rawJobData.shifts.map((shift) => ({
            jobId:safe(rawJobData.id),
            id: safe(shift.id),
            date: safe(shift.date),
            startTime: safe(shift.startTime),
            endTime: safe(shift.endTime),
            userStartTime: safe(shift.userStartTime),
            userEndTime: safe(shift.userEndTime),
            userBreakMinutes: safe(shift.userBreakMinutes, 0),
            status: safe(shift.status),
            confirmedHours: safe(shift.confirmedHours, 0),
            travelCompensation: safe(shift.travelCompensation, 0),
            adminUpdatedTravelCompensation: safe(shift.adminUpdatedTravelCompensation, 0),
            jobMaxTravelCompensation: safe(shift.jobMaxTravelCompensation, 0),
            distance: safe(shift.distance, 0),
            historyDistance: safe(shift.historyDistance, 0),
            payRate: safe(shift.payRate, 0),
            payRateSuper: safe(shift.payRateSuper, 0),
            payRateExecSuper: safe(shift.payRateExecSuper, 0),
            superRate: safe(shift.superRate, 0),
            shiftApplicationType: safe(shift.shiftApplicationType),
            applicationSelected: safe(shift.applicationSelected, false),
            guestId: safe(shift.guestId),
            shiftBookedAsInternalStaff: safe(shift.shiftBookedAsInternalStaff, false),
            isVisibileAsInternalLocum: safe(shift.isVisibileAsInternalLocum, false),
            abilities: safe(shift.abilities, []),
            totalShiftApplications: safe(shift.totalShiftApplications, 0),
            userId: safe(shift.userId),
            applicationRadius: safe(shift.applicationRadius, 0),
            recommendedRate: safe(shift.recommendedRate, 0),
          locum: safe(removeKey(shift.locum, "locumAsJobSeekerSetting"), null),
            job: rawJobData.settings
              ? {
                  id: safe(rawJobData.id),
                  minRate: safe(rawJobData.minRate),
                  maxRate: safe(rawJobData.maxRate),
                  weekendMinRate: safe(rawJobData.weekendMinRate),
                  weekendMaxRate: safe(rawJobData.weekendMaxRate),
                  saturdayMinRate: safe(rawJobData.saturdayMinRate),
                  saturdayMaxRate: safe(rawJobData.saturdayMaxRate),
                  sundayMinRate: safe(rawJobData.sundayMinRate),
                  sundayMaxRate: safe(rawJobData.sundayMaxRate),
                  platformRoleId: safe(rawJobData.platformRoleId),
                  covidCertRequired: safe(rawJobData.covidCertRequired, false),
                  maxTravelCompensation: safe(rawJobData.maxTravelCompensation, 0),
                  enableTravelAllowanceForFirstAndLastDay: safe(rawJobData.enableTravelAllowanceForFirstAndLastDay, false),
                  isBlockShift: safe(rawJobData.isBlockShift, false),
                  settings: rawJobData.settings
                    ? {
                        close: safe(rawJobData.settings.close, null),
                        openUp: safe(rawJobData.settings.openUp, null),
                        solo: safe(rawJobData.settings.solo, false),
                        parking: safe(rawJobData.settings.parking, false),
                        notes: safe(rawJobData.settings.notes, ""),
                        adminAids: safe(rawJobData.settings.adminAids, []),
                        numOfTech: safe(rawJobData.settings.numOfTech, 0),
                        payingForTravel: safe(rawJobData.settings.payingForTravel, false),
                        payForAccommodation: safe(rawJobData.settings.payForAccommodation, false),
                        payForFlights: safe(rawJobData.settings.payForFlights, false),
                        languages: safe(rawJobData.settings.languages, [])
                      }
                    : null
                }
              : null,

            visibilities: shift.visibilities
              ? {
                  visibilityStatus: safe(shift.visibilities.visibilityStatus),
                  agencies: safe(shift.visibilities.agencies, []),
                  moreOptionAgency: safe(shift.visibilities.moreOptionAgency, null),
                  moreOptionOpenMarketplace: shift.visibilities.moreOptionOpenMarketplace
                    ? {
                        value: safe(shift.visibilities.moreOptionOpenMarketplace.value),
                        onboarding: safe(shift.visibilities.moreOptionOpenMarketplace.onboarding, false)
                      }
                    : null,
                  moreOptionInternalPools: safe(shift.visibilities.moreOptionInternalPools, null),
                  guestLocums: safe(shift.visibilities.guestLocums, []),
                  moreOptionPreferredLocum: safe(shift.visibilities.moreOptionPreferredLocum, null),
                  locums: safe(shift.visibilities.locums, []),
                  visibilityTags: shift.visibilities.visibilityTags
                    ? shift.visibilities.visibilityTags.map((tag) => ({ id: safe(tag.id) }))
                    : []
                }
              : null,

            agency: safe(shift.agency, null),
            shiftApplications: shift.shiftApplications
              ? shift.shiftApplications.map((app) => ({
                  id: safe(app.id),
                  locumRate: safe(app.locumRate, 0),
                  status: safe(app.status),
                  statusLabel: safe(app.statusLabel),
                  locum: safe(app.locum, null)
                }))
              : [],
            applicationSelectedBy: shift.applicationSelectedBy
              ? {
                  id: safe(shift.applicationSelectedBy.id),
                  status: safe(shift.applicationSelectedBy.status),
                  locum: safe(shift.applicationSelectedBy.locum, null)
                }
              : null,
            shiftAgencyLocum: safe(shift.shiftAgencyLocum, null),
            pharmacy: safe(shift.pharmacy, null)
          }))
        : [],

      __optimized: true
    },

    __optimized: true
  };

  // Preserve other top-level properties
  if (jobData !== rawJobData) {
    Object.keys(jobData).forEach((key) => {
      if (key !== "job" && !(key in essentialData)) {
        essentialData[key] = jobData[key];
      }
    });
  }

  return essentialData;
};
